import React, { useEffect, useState } from 'react';
import { Btn, Loading, Msg, Section, panelPost, useAdminGet } from '../kit';

/**
 * For You · Settings (app_settings 'foryou_settings', Backend/foryou.py).
 * Every number has server-side bounds; a value outside them is refused on
 * save (400) and ignored if it was ever stored.
 */

const S = 'foryou';
type Settings = Record<string, any>;

const NUM_GROUPS: { title: string; note?: string; rows: [string, string, string?][] }[] = [
  { title: 'Page', rows: [
    ['max_problems', 'Problem statements per member', 'The profile holds three (1 + 2 additional).'],
    ['max_chars', 'Characters per statement'],
    ['max_keywords', 'Interest keywords per member'],
    ['top_shown', 'Recommendations shown per statement'],
    ['full_list_max', '"View all" list length (matches kept per statement)'],
  ] },
  { title: 'Matching (LLM relevance, 0–100)', note: 'Jobs run in the background only; a page view never calls the LLM.', rows: [
    ['strong_match', '"Strong match" from (%)'],
    ['match_min', 'Keep a statement match from (%)'],
    ['pool_min', 'Keyword pool threshold (%)'],
    ['pool_max', 'Pool size cap'],
    ['problem_candidates', 'Candidates scored per statement'],
    ['pool_candidates', 'Candidates scored per keyword'],
  ] },
  { title: 'Weekly pick', rows: [
    ['rollover_hour', 'Rollover hour (0–23)'],
    ['rollover_minute', 'Rollover minute'],
  ] },
  { title: 'Share', rows: [
    ['share_max_recipients', 'Recipients per message'],
    ['share_max_techs', 'Technologies per message'],
    ['share_note_max', 'Note length (characters)'],
    ['share_per_hour', 'Sends per member per hour'],
    ['share_per_day', 'Sends per member per day'],
  ] },
  { title: 'Jobs', rows: [
    ['worker_concurrency', 'Jobs at once (per API process)'],
    ['daily_job_cap', 'Jobs per UTC day (0 = no cap)'],
    ['max_attempts', 'Attempts before a job stays failed'],
    ['retry_minutes', 'Retry after (minutes × attempt)'],
  ] },
];
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const SECTION_LABELS: Record<string, string> = {
  problems: 'Problem statements', recommended: 'Recommended for this problem', saved: 'Saved technologies',
  interests: 'Interests · featured this week',
};

const ForYouSettings: React.FC = () => {
  const res = useAdminGet<any>(S, 'settings');
  const [draft, setDraft] = useState<Settings | null>(null);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (res.data) setDraft(JSON.parse(JSON.stringify(res.data.settings))); }, [res.data]);
  if (res.loading && !draft) return <Loading />;
  if (res.error) return <Msg kind="error">{res.error}</Msg>;
  if (!draft) return null;
  const bounds: Record<string, [number, number]> = res.data?.bounds || {};
  const est = res.data?.estimate || {};
  const set = (k: string, v: any) => setDraft(d => ({ ...(d || {}), [k]: v }));
  const dirty = JSON.stringify(draft) !== JSON.stringify(res.data?.settings);

  const save = async () => {
    setSaving(true); setMsg(null);
    const r = await panelPost(S, 'settings_set', { settings: draft });
    setSaving(false);
    if (r.error) { setMsg({ kind: 'error', text: r.error }); return; }
    setMsg({ kind: 'ok', text: 'Saved. Members see the change within a minute.' });
    res.reload();
  };

  const num = (k: string, label: string, hint?: string) => (
    <label key={k} className="flex flex-col gap-1 text-sm">
      <span className="text-grayscale-1200">{label}</span>
      <input type="number" value={draft[k] ?? ''} min={bounds[k]?.[0]} max={bounds[k]?.[1]}
             onChange={e => set(k, e.target.value === '' ? '' : Number(e.target.value))}
             className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm w-40" />
      <span className="text-[11px] text-grayscale-1100">
        {bounds[k] ? `${bounds[k][0]}–${bounds[k][1]}` : ''}{hint ? ` · ${hint}` : ''}
      </span>
    </label>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Btn kind="primary" onClick={save} disabled={!dirty || saving}>{saving ? 'Saving…' : 'Save settings'}</Btn>
        {dirty && <Btn onClick={() => setDraft(JSON.parse(JSON.stringify(res.data.settings)))}>Discard changes</Btn>}
        <span className="text-xs text-grayscale-1100">
          This week: {res.data?.week?.label} · LLM calls per statement ≈ {est.per_problem}, per keyword pool ≈ {est.per_pool}
          {' '}(rescore chunk {est.rescore_chunk})
        </span>
      </div>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}

      <Section title="Sections shown">
        <div className="flex flex-wrap gap-4">
          {Object.keys(SECTION_LABELS).map(s => (
            <label key={s} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!draft.sections?.[s]}
                     onChange={e => set('sections', { ...(draft.sections || {}), [s]: e.target.checked })} />
              {SECTION_LABELS[s]}
            </label>
          ))}
        </div>
      </Section>

      {NUM_GROUPS.map(g => (
        <Section key={g.title} title={g.title}>
          {g.note && <p className="text-xs text-grayscale-1100">{g.note}</p>}
          <div className="flex flex-wrap gap-5">
            {g.title === 'Weekly pick' && (
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-grayscale-1200">Rollover day</span>
                <select value={draft.rollover_weekday} onChange={e => set('rollover_weekday', Number(e.target.value))}
                        className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm w-40">
                  {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
                <span className="text-[11px] text-grayscale-1100">Spec: Monday 00:00 IST</span>
              </label>
            )}
            {g.rows.map(([k, label, hint]) => num(k, label, hint))}
            {g.title === 'Weekly pick' && (
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-grayscale-1200">Time zone</span>
                <input type="text" value={draft.rollover_tz || ''} onChange={e => set('rollover_tz', e.target.value)}
                       className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm w-48" />
                <span className="text-[11px] text-grayscale-1100">e.g. Asia/Kolkata</span>
              </label>
            )}
            {g.title.startsWith('Matching') && (
              <label className="flex items-center gap-2 text-sm self-center">
                <input type="checkbox" checked={!!draft.expand_problems}
                       onChange={e => set('expand_problems', e.target.checked)} />
                Expand statements before searching (1 extra LLM call, cached per text)
              </label>
            )}
            {g.title === 'Jobs' && (
              <label className="flex items-center gap-2 text-sm self-center">
                <input type="checkbox" checked={!!draft.auto_build_missing_pools}
                       onChange={e => set('auto_build_missing_pools', e.target.checked)} />
                Build a keyword's pool when the first member adds it
              </label>
            )}
          </div>
        </Section>
      ))}
    </div>
  );
};

export default ForYouSettings;
